import {useState, useEffect, useRef} from "react";
import {ScrollShadow} from "@nextui-org/react";
import {BiCalendar, BiChevronLeft, BiChevronRight} from "react-icons/bi";
import {LiaBirthdayCakeSolid} from "react-icons/lia";
import {GiHeartWings, GiPartyPopper} from "react-icons/gi";
import {MdEvent} from "react-icons/md";
import {TbGrave} from "react-icons/tb";
import SharedService from "@/services/shared-service";
import {getDailyEvents} from "@/services/home-service";
import {LEVEL_CLERGY_MAP} from "@/configuration/static-data-provider";
import Link from "next/link";
import moment from "moment";

/**
 * Compact Events Calendar Component
 * Displays daily events, masses, birthdays, priest ordinations, saints, etc.
 *
 * @param {Object} initialData - Optional initial event data
 */
export default function EventsCalendarCompact({initialData = null}) {
    const [selectedDate, setSelectedDate] = useState(SharedService.moment());
    const [detail, setDetail] = useState({
        today: null,
        description: null,
        birth: null,
        priest: null,
        saint: null,
        masses: null,
        scheduleEvents: null,
        rip: null
    });
    const [isLoading, setIsLoading] = useState(false);
    const dateRef = useRef(null);

    async function onClickNextDay() {
        const newDate = selectedDate.clone().add(1, "day");
        setSelectedDate(newDate);
        await renderComponentByDate(newDate);
    }

    async function onClickPreviousDay() {
        const newDate = selectedDate.clone().add(-1, "day");
        setSelectedDate(newDate);
        await renderComponentByDate(newDate);
    }

    async function renderComponentByDate(dateOverride = null) {
        setIsLoading(true);
        const date = dateOverride || selectedDate;
        try {
            let dailyEvents = await getDailyEvents(date);
            setDetail({
                today: date.format("DD-MM-YYYY"),
                rip: dailyEvents.rip.map(e => `${LEVEL_CLERGY_MAP[e.level]} ${e.entitySTName} ${e.entityName}`),
                birth: dailyEvents.birth.map(e => `${LEVEL_CLERGY_MAP[e.level]} ${e.entitySTName} ${e.entityName}`),
                priest: dailyEvents.priest.map(e => `${LEVEL_CLERGY_MAP[e.level]} ${e.entitySTName} ${e.entityName}`),
                saint: dailyEvents.saint.map(e => `${LEVEL_CLERGY_MAP[e.level]} ${e.entitySTName} ${e.entityName}`),
                masses: dailyEvents.masses,
                description: dailyEvents.masses.length > 0 ? dailyEvents.masses[0].description : null,
                scheduleEvents: dailyEvents.scheduleEvents
            });
        } catch (error) {
            console.error("Error fetching events:", error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (initialData) {
            setDetail(initialData);
        } else {
            renderComponentByDate();
        }
    }, []);

    const hasEvents = detail.masses?.length > 0 ||
        detail.scheduleEvents?.length > 0 ||
        detail.birth?.length > 0 ||
        detail.priest?.length > 0 ||
        detail.saint?.length > 0 ||
        detail.rip?.length > 0;

    const handleChooseDate = () => {
        if (dateRef.current) {
            dateRef.current.showPicker?.();
        }
    };

    function handleDateChange(event) {
        if (!event.target.value) return;
        const selected = moment(event.target.value);
        setSelectedDate(selected);
        renderComponentByDate(selected);
    }

    useEffect(() => {
        renderComponentByDate();
    }, []);

    return (
        <div className="bg-white rounded-[5px] shadow-md h-full flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-primary p-4 text-center flex items-center justify-center gap-2">
                <BiCalendar className="text-yellow-300 text-2xl"/>
                <h2 className="text-white font-bold text-lg uppercase">SỰ KIỆN</h2>
            </div>

            {/* Date Navigation */}
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 bg-gray-50">
                <button
                    onClick={onClickPreviousDay}
                    className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                    aria-label="Previous day"
                >
                    <BiChevronLeft className="w-5 h-5 text-gray-700"/>
                </button>

                <div className="flex flex-col items-center">
                    <div className="text-xs text-gray-500 uppercase mb-1">
                        {SharedService.getWeekDay(selectedDate.format("dddd"))}
                    </div>
                    {/*tạm ẩn*/}
                    {/*{detail.masses && detail.masses.map((item, index) => {*/}
                    {/*    if (index >= 1)*/}
                    {/*        return (*/}
                    {/*            <div className="text-sm font-bold text-primary" key={index}>*/}
                    {/*                {item.tenLe}*/}
                    {/*            </div>*/}
                    {/*        )*/}
                    {/*})}*/}
                    <input type="date"
                           ref={dateRef}
                           className="mt-1 text-xs text-gray-600 text-center bg-transparent focus:outline-none focus:ring-0 border-none hide-calendar-icon select-none"
                           value={selectedDate.format("YYYY-MM-DD")}
                           onClick={handleChooseDate}
                           onChange={handleDateChange}
                    />
                </div>

                <button
                    onClick={onClickNextDay}
                    className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                    aria-label="Next day"
                >
                    <BiChevronRight className="w-5 h-5 text-gray-700"/>
                </button>
            </div>

            {/* Events Content */}
            <ScrollShadow hideScrollBar className="flex-1 h-[400px]">
                <div className="p-4 space-y-4">
                    {isLoading ? (
                        <div className="text-center text-gray-500 py-8">
                            Đang tải...
                        </div>
                    ) : !hasEvents ? (
                        <div className="text-center text-gray-500 py-8">
                            Không có sự kiện
                        </div>
                    ) : (
                        <>
                            {/* Liturgy/Mass Information */}
                            {detail.masses && detail.masses.map((item, index) => (
                                <div key={index} className="border-l-3 border-primary pl-3">
                                    {item.link ? (
                                        <Link
                                            className="font-semibold text-primary hover:underline block"
                                            dangerouslySetInnerHTML={{__html: item.tenLe}}
                                            href={item.linkType === 'articles' ? SharedService.getPostPath(item) : item.link}
                                            target="_blank"
                                        />
                                    ) : (
                                        <h3
                                            className="font-semibold text-gray-800"
                                            dangerouslySetInnerHTML={{__html: item.tenLe}}
                                        />
                                    )}
                                    {item.description && (
                                        <p className="text-xs text-gray-600 italic mt-1">{item.description}</p>
                                    )}
                                </div>
                            ))}

                            {/* Description */}
                            {detail.description && (
                                <p className="text-xs text-primary italic">{detail.description}</p>
                            )}

                            {/* RIP */}
                            {detail.rip && detail.rip.length > 0 && (
                                <div className="bg-purple-50 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-purple-900 font-semibold mb-2">
                                        <TbGrave className="w-4 h-4"/>
                                        <span className="text-sm">Ngày mất:</span>
                                    </div>
                                    <p className="text-xs text-purple-700 leading-relaxed">
                                        {detail.rip.join(', ')}
                                    </p>
                                </div>
                            )}

                            {/* Birthdays */}
                            {detail.birth && detail.birth.length > 0 && (
                                <div className="bg-yellow-50 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-yellow-900 font-semibold mb-2">
                                        <LiaBirthdayCakeSolid className="w-4 h-4"/>
                                        <span className="text-sm">Mừng sinh nhật:</span>
                                    </div>
                                    <p className="text-xs text-yellow-700 leading-relaxed">
                                        {detail.birth.join(', ')}
                                    </p>
                                </div>
                            )}

                            {/* Priest Ordination Anniversary */}
                            {detail.priest && detail.priest.length > 0 && (
                                <div className="bg-blue-50 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-primary font-semibold mb-2">
                                        <GiPartyPopper className="w-4 h-4"/>
                                        <span className="text-sm">Kỷ niệm thụ phong Linh Mục:</span>
                                    </div>
                                    <p className="text-xs text-blue-700 leading-relaxed">
                                        {detail.priest.join(', ')}
                                    </p>
                                </div>
                            )}

                            {/* Saint's Day */}
                            {detail.saint && detail.saint.length > 0 && (
                                <div className="bg-pink-50 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-pink-900 font-semibold mb-2">
                                        <GiHeartWings className="w-4 h-4"/>
                                        <span className="text-sm">Mừng bổn mạng:</span>
                                    </div>
                                    <p className="text-xs text-pink-700 leading-relaxed">
                                        {detail.saint.join(', ')}
                                    </p>
                                </div>
                            )}

                            {/* Schedule Events */}
                            {detail.scheduleEvents && detail.scheduleEvents.length > 0 && (
                                <div className="bg-orange-50 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-orange-900 font-semibold mb-2">
                                        <MdEvent className="w-4 h-4"/>
                                        <span className="text-sm">Sự kiện:</span>
                                    </div>
                                    <div className="space-y-2">
                                        {detail.scheduleEvents.map((e, index) => (
                                            <div key={index} className="text-xs text-gray-700 leading-relaxed">
                                                {e.link && e.link.length > 0 ? (
                                                    <Link className="font-semibold text-primary hover:underline"
                                                          href={e.link}>
                                                        {e.name}
                                                    </Link>
                                                ) : (
                                                    <span className="font-semibold">{e.name}</span>
                                                )}
                                                {' - '}
                                                {e.entityType === 'organization' ? (
                                                    <Link className="text-primary hover:underline"
                                                          href={`/nha-tho/${e.entityID}`}>
                                                        {e.quotation}
                                                        {e.code !== 'chau_thanh_the' && (
                                                            <span className="text-gray-600">
                                                                {' '}(Từ {SharedService.formatISODate(e.startDate, "HH:mm")} ngày {SharedService.formatISODate(e.startDate, "DD/MM/YYYY")} đến {SharedService.formatISODate(e.endDate, "HH:mm")} ngày {SharedService.formatISODate(e.endDate, "DD/MM/YYYY")})
                                                            </span>
                                                        )}
                                                    </Link>
                                                ) : (
                                                    <span>
                                                        {e.quotation}
                                                        {e.code !== 'chau_thanh_the' && (
                                                            <span className="text-gray-600">
                                                                {' '}(Từ {SharedService.formatISODate(e.startDate, "HH:mm")} ngày {SharedService.formatISODate(e.startDate, "DD/MM/YYYY")} đến {SharedService.formatISODate(e.endDate, "HH:mm")} ngày {SharedService.formatISODate(e.endDate, "DD/MM/YYYY")})
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </ScrollShadow>

            {/*TODO: Footer - View All Link */}
            <div className="border-t border-gray-200 p-3 bg-gray-50">
                <Link
                    href="/su-kien"
                    className="text-primary hover:underline text-sm font-medium flex items-center justify-center gap-1"
                >
                    <span>Xem tất cả</span>
                    <span>→</span>
                </Link>
            </div>
        </div>
    );
}
